import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail, setToken } from "../lib/api";

export default function SignupAgencyInvite() {
  const { token } = useParams();
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/agency/team/invites/lookup/${token}`);
        setLookup(data);
        if (data.prefilled_email) setEmail(data.prefilled_email);
      } catch (err) {
        setLookup({ valid: false, reason: "not_found" });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/signup/agency/invite/${token}`, { name: name.trim(), email: email.trim(), password });
      setToken(data.access_token);
      await refresh();
      toast.success(t("invite_signup.welcome_toast", { org: lookup.org_name }));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "SEAT_CAP_REACHED") toast.error(t("invite_signup.seat_cap"));
      else toast.error(formatApiErrorDetail(detail) || t("common.error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="invite-signup-page">
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-md px-6 py-16">
        <Link to="/" className="text-sm font-medium text-slate-500 hover:text-slate-800">← {t("common.back_home")}</Link>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          {loading ? (
            <p className="text-sm text-slate-500">{t("common.loading")}</p>
          ) : !lookup?.valid ? (
            <>
              <p className="text-xs font-semibold uppercase tracking-wider text-rose-700">{t("invite_signup.invalid_eyebrow")}</p>
              <h1 className="mt-2 text-2xl font-semibold text-slate-900" data-testid="invite-invalid-title">
                {t(`invite_signup.invalid_${lookup?.reason || "not_found"}`)}
              </h1>
              <p className="mt-2 text-sm text-slate-600">{t("invite_signup.invalid_body")}</p>
              <Link to="/" className="mt-6 inline-flex rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700" data-testid="invite-home-cta">
                {t("common.back_home")}
              </Link>
            </>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("invite_signup.eyebrow")}</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900" data-testid="invite-title">
                {t("invite_signup.join_team", { org: lookup.org_name })}
              </h1>
              <p className="mt-2 text-sm text-slate-600">{t("invite_signup.body")}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.contact_name")}</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="invite-name" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.email")}</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="invite-email" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.password")}</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="invite-password" />
                </div>
                <button type="submit" disabled={busy} className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60" data-testid="invite-submit">
                  {busy ? t("common.loading") : t("invite_signup.submit")}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
